import { useState, useEffect, useCallback, useRef } from 'react';
import apiClient from '@/lib/api/client';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';

interface PhotoGalleryTabProps {
  jobId: number;
}

interface JobDocument {
  id: number;
  job_id: number;
  file_name: string;
  file_path: string;
  file_type: string;
  file_size: number;
  is_photo: number | boolean;
  caption?: string;
  milestone?: string;
  url?: string;
  created_at: string;
}

const API_BASE = import.meta.env.VITE_API_URL || '/api';
const UPLOADS_BASE = API_BASE.replace(/\/api$/, '') || '';

const photoSrc = (doc: JobDocument): string => {
  if (doc.url) return doc.url;
  return `${UPLOADS_BASE}/uploads/${doc.file_path}`;
};

// ── Photo card ────────────────────────────────────────────────────────────────

interface PhotoCardProps {
  doc: JobDocument;
  onDelete: (id: number) => void;
  deleting: boolean;
}

const PhotoCard = ({ doc, onDelete, deleting }: PhotoCardProps) => {
  const [imgError, setImgError] = useState(false);
  const src = photoSrc(doc);

  return (
    <div className="group relative rounded-xl overflow-hidden border border-gray-200 bg-gray-100 aspect-square">
      {!imgError ? (
        <img
          src={src}
          alt={doc.caption || doc.file_name}
          className="w-full h-full object-cover"
          onError={() => setImgError(true)}
        />
      ) : (
        <div className="flex flex-col items-center justify-center w-full h-full text-gray-400 p-4">
          <span className="material-symbols-outlined text-4xl mb-1">broken_image</span>
          <p className="text-xs text-center truncate w-full">{doc.file_name}</p>
        </div>
      )}

      {/* Hover overlay */}
      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
        <a
          href={src}
          target="_blank"
          rel="noopener noreferrer"
          className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
          title="View full size"
        >
          <span className="material-symbols-outlined text-white text-xl leading-none">open_in_new</span>
        </a>
        <button
          onClick={() => onDelete(doc.id)}
          disabled={deleting}
          className="p-2 bg-red-500/40 rounded-lg hover:bg-red-500/60 transition-colors disabled:opacity-40"
          title="Delete photo"
        >
          <span className="material-symbols-outlined text-white text-xl leading-none">delete</span>
        </button>
      </div>

      {/* Caption / milestone badges */}
      {(doc.caption || doc.milestone) && (
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-2">
          {doc.milestone && (
            <span className="inline-block text-[10px] font-semibold bg-primary/90 text-white rounded px-1.5 py-0.5 mb-0.5">
              {doc.milestone}
            </span>
          )}
          {doc.caption && (
            <p className="text-xs text-white truncate">{doc.caption}</p>
          )}
        </div>
      )}
    </div>
  );
};

// ── Upload form ───────────────────────────────────────────────────────────────

interface UploadFormProps {
  jobId: number;
  onUploaded: () => void;
  onCancel: () => void;
}

const UploadForm = ({ jobId, onUploaded, onCancel }: UploadFormProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [caption, setCaption] = useState('');
  const [milestone, setMilestone] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) { setError('Please select a photo.'); return; }
    setUploading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('job_id', String(jobId));
      formData.append('is_photo', '1');
      if (caption) formData.append('caption', caption);
      if (milestone) formData.append('milestone', milestone);

      await apiClient.post('/documents', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onUploaded();
    } catch {
      setError('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
      <h3 className="text-sm font-bold text-gray-900">Upload Photo</h3>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">{error}</div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* File picker */}
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">
            Photo <span className="text-red-500">*</span>
          </label>
          {preview ? (
            <div className="relative w-32 h-32 rounded-lg overflow-hidden border border-gray-200">
              <img src={preview} alt="Preview" className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => { setFile(null); setPreview(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                className="absolute top-1 right-1 p-0.5 bg-black/50 rounded-full text-white hover:bg-black/70 transition-colors"
              >
                <span className="material-symbols-outlined text-base leading-none">close</span>
              </button>
            </div>
          ) : (
            <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-200 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
              <span className="material-symbols-outlined text-3xl text-gray-400 mb-1">add_photo_alternate</span>
              <p className="text-xs text-gray-500"><span className="font-semibold">Click to select</span> an image</p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileChange}
              />
            </label>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Caption</label>
            <input
              type="text"
              value={caption}
              onChange={e => setCaption(e.target.value)}
              disabled={uploading}
              placeholder="Optional caption"
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-60"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Milestone</label>
            <input
              type="text"
              value={milestone}
              onChange={e => setMilestone(e.target.value)}
              disabled={uploading}
              placeholder="e.g. Framing complete"
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-60"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={uploading}
            className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={uploading || !file}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white text-sm font-bold hover:bg-primary/90 disabled:opacity-60 transition-colors"
          >
            {uploading ? (
              <>
                <span className="material-symbols-outlined text-base leading-none animate-spin">progress_activity</span>
                Uploading…
              </>
            ) : (
              <>
                <span className="material-symbols-outlined text-base leading-none">upload</span>
                Upload
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

// ── Main tab ──────────────────────────────────────────────────────────────────

export const PhotoGalleryTab = ({ jobId }: PhotoGalleryTabProps) => {
  const [photos, setPhotos] = useState<JobDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [confirmDeletePhotoId, setConfirmDeletePhotoId] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const { data } = await apiClient.get('/documents', { params: { jobId } });
      const docs: JobDocument[] = data.data ?? data ?? [];
      setPhotos(docs.filter(d => d.is_photo === 1 || d.is_photo === true));
    } catch {
      setError('Failed to load photos.');
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = (id: number) => {
    setConfirmDeletePhotoId(id);
  };

  const confirmPhotoDelete = async () => {
    if (!confirmDeletePhotoId) return;
    const id = confirmDeletePhotoId;
    setConfirmDeletePhotoId(null);
    setDeletingId(id);
    try {
      await apiClient.delete(`/documents/${id}`);
      setPhotos(prev => prev.filter(p => p.id !== id));
    } catch {
      setError('Failed to delete photo.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleUploaded = async () => {
    setShowUpload(false);
    await load();
  };

  return (
    <div className="space-y-4">
      <DailyProgressPanel jobId={jobId} />
      <div className="flex items-center justify-between">
        <h2 className="text-gray-900 text-[22px] font-bold leading-tight tracking-[-0.015em]">
          Photo Gallery
        </h2>
        {!showUpload && (
          <button
            onClick={() => setShowUpload(true)}
            className="flex items-center gap-2 h-9 px-4 rounded-lg bg-primary text-white text-sm font-bold hover:bg-primary/90 transition-colors"
          >
            <span className="material-symbols-outlined text-base">add_photo_alternate</span>
            Upload Photo
          </button>
        )}
      </div>

      {error && (
        <div className="flex items-center justify-between gap-4 rounded-lg border border-red-200 bg-red-50 p-3">
          <p className="text-sm text-red-700">{error}</p>
          <button onClick={load} className="text-sm font-bold text-red-700 hover:underline">Retry</button>
        </div>
      )}

      {showUpload && (
        <UploadForm
          jobId={jobId}
          onUploaded={handleUploaded}
          onCancel={() => setShowUpload(false)}
        />
      )}

      {loading ? (
        <div className="py-12 text-center">
          <span className="material-symbols-outlined animate-spin text-3xl text-gray-300 block mx-auto mb-2">progress_activity</span>
          <p className="text-sm text-gray-400">Loading photos…</p>
        </div>
      ) : photos.length === 0 ? (
        <div className="py-16 bg-white rounded-xl border border-gray-200 text-center">
          <span className="material-symbols-outlined text-6xl text-gray-300 block mb-3">photo_library</span>
          <p className="text-sm font-medium text-gray-500 mb-1">No photos yet — upload the first one</p>
          <p className="text-xs text-gray-400 mb-4">Document job progress, milestones, and completed work</p>
          {!showUpload && (
            <button
              onClick={() => setShowUpload(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white text-sm font-bold hover:bg-primary/90 transition-colors"
            >
              <span className="material-symbols-outlined text-base">add_photo_alternate</span>
              Upload First Photo
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {photos.map(photo => (
            <PhotoCard
              key={photo.id}
              doc={photo}
              onDelete={handleDelete}
              deleting={deletingId === photo.id}
            />
          ))}
        </div>
      )}

      {photos.length > 0 && (
        <p className="text-xs text-gray-400 text-right">
          {photos.length} photo{photos.length !== 1 ? 's' : ''}
        </p>
      )}

      {confirmDeletePhotoId !== null && (
        <ConfirmDialog
          title="Delete Photo"
          message="Delete this photo? This cannot be undone."
          confirmLabel="Delete"
          variant="danger"
          onConfirm={confirmPhotoDelete}
          onCancel={() => setConfirmDeletePhotoId(null)}
        />
      )}
    </div>
  );
};


// ── Daily progress reports: share toggle + per-day AI summaries ─────────────
interface ProgressDay { date: string; photo_count: number; summary: string | null; photos: { id: number; url: string; caption?: string }[] }
function DailyProgressPanel({ jobId }: { jobId: number }) {
  const [shared, setShared] = useState(false);
  const [days, setDays] = useState<ProgressDay[]>([]);
  const [busyDate, setBusyDate] = useState<string | null>(null);
  const [msg, setMsg] = useState('');

  const load = useCallback(() => {
    apiClient.get(`/jobs/${jobId}/progress-reports`).then(r => {
      setShared(!!r.data.data?.shared);
      setDays(r.data.data?.reports ?? []);
    }).catch(() => {});
  }, [jobId]);
  useEffect(() => { load(); }, [load]);

  const toggleShare = async () => {
    const next = !shared;
    setShared(next);
    try { await apiClient.put(`/jobs/${jobId}/progress-sharing`, { enabled: next }); }
    catch { setShared(!next); }
  };

  const summarize = async (date: string) => {
    setBusyDate(date);
    setMsg('');
    try {
      const r = await apiClient.post(`/jobs/${jobId}/progress-reports/${date}/summarize`, {});
      setDays(ds => ds.map(d => d.date === date ? { ...d, summary: r.data.data.summary } : d));
    } catch {
      setMsg('Summary failed — try again.');
    } finally { setBusyDate(null); }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-sm font-bold text-gray-900 flex items-center gap-1.5">
            <span className="material-symbols-outlined text-[#C77D3F] text-lg">photo_album</span>
            Daily progress reports
          </h3>
          <p className="text-[11px] text-gray-400">Each day's photos bundle into a dated update. AI writes the client-facing summary.</p>
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <span className="text-xs font-semibold text-gray-600">Share with client</span>
          <button onClick={toggleShare}
            className={`relative w-11 h-6 rounded-full transition-colors ${shared ? 'bg-[#C77D3F]' : 'bg-gray-300'}`}>
            <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${shared ? 'translate-x-5' : ''}`} />
          </button>
        </label>
      </div>
      {msg && <p className="text-xs text-red-600">{msg}</p>}
      {days.length === 0 ? (
        <p className="text-xs text-gray-400">No photo days yet — reports appear as crews upload photos.</p>
      ) : days.map(d => (
        <div key={d.date} className="border border-gray-100 rounded-xl p-3">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-semibold text-gray-900">
              {new Date(d.date + 'T12:00:00').toLocaleDateString('en-CA', { weekday: 'long', month: 'short', day: 'numeric' })}
              <span className="text-xs text-gray-400 font-normal ml-1.5">{d.photo_count} photo{d.photo_count !== 1 ? 's' : ''}</span>
            </p>
            <button onClick={() => summarize(d.date)} disabled={busyDate === d.date}
              className="h-7 px-2.5 rounded-lg border border-[#C77D3F]/40 text-[11px] font-bold text-[#C77D3F] flex items-center gap-1 disabled:opacity-50">
              <span className={`material-symbols-outlined text-sm ${busyDate === d.date ? 'animate-spin' : ''}`}>
                {busyDate === d.date ? 'progress_activity' : 'neurology'}
              </span>
              {d.summary ? 'Rewrite summary' : 'AI summary'}
            </button>
          </div>
          {d.summary && <p className="text-xs text-gray-600 mt-1.5 leading-relaxed">{d.summary}</p>}
          <div className="flex gap-1.5 mt-2 overflow-x-auto">
            {d.photos.map(ph => (
              <img key={ph.id} src={ph.url} alt={ph.caption || ''} loading="lazy"
                className="h-16 w-16 rounded-lg object-cover border border-gray-100 flex-shrink-0" />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}